// ─────────────────────────────────────────────────────────
// store/uiStore.ts — UI state: toasts, theme, modals
// ─────────────────────────────────────────────────────────
import { create } from 'zustand'
import { FLAGS }  from '@/lib/flags'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id:      string
  message: string
  type:    ToastType
}

interface UIState {
  theme:     'light' | 'dark'
  toasts:    Toast[]
  isSyncing: boolean
  syncMsg:   string
  syncError: string | null   // persistent banner when writes are dead-lettered

  // ── Sidebar C3 nav state ──────────────────────────────────
  navFlyoutPinned: boolean   // true = 312px; false = 64px rail-only

  setTheme:            (theme: 'light' | 'dark') => void
  toggleTheme:         () => void
  showToast:           (message: string, type?: ToastType) => void
  removeToast:         (id: string) => void
  setSyncing:          (syncing: boolean, msg?: string) => void
  setSyncError:        (msg: string) => void
  clearSyncError:      () => void
  setNavFlyoutPinned:  (v: boolean) => void
}

// Force light theme on init if dark mode is disabled
const _initialTheme: 'light' | 'dark' = FLAGS.DARK_MODE_ENABLED
  ? ((localStorage.getItem('theme') as 'light' | 'dark') ?? 'light')
  : 'light'

// Flyout is pinned by default; only unpinned if user explicitly set it
const _initNavFlyoutPinned = localStorage.getItem('cbd_nav_pinned') !== 'false'

export const useUIStore = create<UIState>((set, get) => ({
  theme:           _initialTheme,
  toasts:          [],
  isSyncing:       false,
  syncMsg:         '',
  syncError:       null,
  navFlyoutPinned: _initNavFlyoutPinned,

  setTheme: (theme) => {
    // Ignore dark requests when dark mode is disabled
    const effective: 'light' | 'dark' = FLAGS.DARK_MODE_ENABLED ? theme : 'light'
    localStorage.setItem('theme', effective)
    document.documentElement.setAttribute('data-theme', effective)
    set({ theme: effective })
  },

  toggleTheme: () => {
    if (!FLAGS.DARK_MODE_ENABLED) return          // no-op when feature is off
    const next = get().theme === 'light' ? 'dark' : 'light'
    get().setTheme(next)
  },

  showToast: (message, type = 'success') => {
    const id = Math.random().toString(36).slice(2, 9)
    // Replace any existing toasts — only one visible at a time
    set(() => ({ toasts: [{ id, message, type }] }))
    // Auto-remove after 3 000 ms
    setTimeout(() => get().removeToast(id), 3_000)
  },

  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  setSyncing: (syncing, msg = 'Syncing…') =>
    set({ isSyncing: syncing, syncMsg: msg }),

  setSyncError:   (msg) => set({ syncError: msg }),
  clearSyncError: ()    => set({ syncError: null }),

  setNavFlyoutPinned: (v) => {
    try { localStorage.setItem('cbd_nav_pinned', String(v)) } catch { /* ignore */ }
    set({ navFlyoutPinned: v })
  },
}))
